import { Link } from "react-router-dom";
import styled, { css } from "styled-components";

import { ReactComponent as AMark } from "MVVM/Assets/Brand/A-Mark-Light.svg";

import { rgba } from "../../../utils";

// The overlays inside a thumbnail restate these rather than inheriting the
// container's clip, so they have to come from one place. See CardThumb.
const THUMB_RADIUS = "8px";
const COMPACT_RADIUS = "6px";

export const Page = styled.div`
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.ice};

  // Selection ships as a browser default that belongs to no design system.
  ::selection {
    background: ${({ theme }) => theme.colors.envyAlpha40};
    color: ${({ theme }) => theme.colors.ice};
  }
  // Capped and centred, but at the wide break rather than standard: 1280 left
  // most of a large monitor empty, and since the clips column is capped the
  // extra all went to the scoreboard anyway. Full bleed is still wrong here,
  // because a scoreboard row that spans 2500px is a very long line to track.
  margin: 0 auto;
  max-width: ${({ theme }) => theme.breaks.wide};
  padding: 24px 32px 64px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    padding: 24px 24px 64px;
  }

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    padding: 24px 16px 64px;
  }
`;

export const Section = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 16px;
`;

export const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.04em;
  margin: 0 0 12px;
  text-transform: uppercase;
`;

// The card is painted on the body cells, not a wrapper, so the header row can
// sit outside it while the columns stay in one table and line up for free.
export const Table = styled.table<{ $compact?: boolean }>`
  border-collapse: separate;
  border-spacing: 0;
  font-size: 16px;
  // Stat columns only line up if the digits are the same width.
  font-variant-numeric: tabular-nums;
  width: 100%;

  th,
  td {
    font-weight: 600;
    // Metrics were colliding at 10px; they need room to read as separate
    // columns rather than one block of digits.
    padding: 11px 14px;
    text-align: right;
    white-space: nowrap;
  }

  // The player column stays put while the stats scroll under it, so a row is
  // never a set of numbers belonging to nobody.
  th:first-child,
  td:first-child {
    left: 0;
    position: sticky;
    z-index: 1;
  }

  // Body cells carry the card colour; the header sits outside the card, on the
  // page background.
  th:first-child {
    background: ${({ theme }) => theme.colors.ned};
  }

  // Below the medium break only six columns survive; the rest come back with
  // the More stats toggle rather than living behind an undiscoverable scroll.
  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    ${({ $compact }) =>
      $compact &&
      css`
        th:nth-child(n + 7),
        td:nth-child(n + 7) {
          display: none;
        }
      `}
  }

  th {
    padding-bottom: 8px;
  }

  // Scoped so the team label in the first cell keeps its own styling without
  // needing !important to win.
  th:not(:first-child) {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  td {
    background: ${({ theme }) => theme.colors.navy200};
  }

  tbody tr:not(:first-child) td {
    border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  }

  tbody tr:first-child td:first-child {
    border-top-left-radius: 10px;
  }

  tbody tr:first-child td:last-child {
    border-top-right-radius: 10px;
  }

  tbody tr:last-child td:first-child {
    border-bottom-left-radius: 10px;
  }

  tbody tr:last-child td:last-child {
    border-bottom-right-radius: 10px;
  }

  // Team label sits flush with the page edge and the rows indent under it, so
  // the scoreboard reads as a list belonging to that heading.
  th:first-child {
    padding-left: 0;
    text-align: left;
    width: 100%;
  }

  td:first-child {
    padding-left: 14px;
    text-align: left;
    width: 100%;
  }

  th:last-child,
  td:last-child {
    padding-right: 22px;
  }

  // Breathing room between the K/D/A group and the rest of the metrics.
  th:nth-child(2),
  td:nth-child(2) {
    padding-right: 26px;
  }
`;
export const TableWrap = styled.div`
  overflow-x: auto;

  // A visible track is the cheapest honest signal that there is more table to
  // the right; the default overlay bar shows nothing until you already scroll.
  scrollbar-color: ${({ theme }) => theme.colors.navy50} transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.navy50};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

export const ViewerRow = styled.tr`
  td {
    background: ${({ theme }) => theme.colors.navy100};
    font-weight: 800;
  }

  // Left edge only, square. An inset shadow rather than a border so it adds no
  // width to the cell and cannot shift the column.
  td:first-child {
    box-shadow: inset 3px 0 0 ${({ theme }) => theme.colors.envy};
  }

  // Three classes to out-specify the table's own corner rules: when the viewer
  // is the first or last row its cells inherited the card radius, which is what
  // made the green read as a rounded edge.
  &&& td {
    border-radius: 0;
  }
`;

export const List = styled.ul`
  font-size: 13px;
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
    display: flex;
    gap: 12px;
    justify-content: space-between;
    padding: 6px 0;
  }
`;

export const Muted = styled.p`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  margin: 0;
`;

export const RankBadgeWrap = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 8px;
`;

export const RankIcon = styled.img`
  height: 20px;
  width: auto;
`;

export const RankSwatch = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color};
  border-radius: 2px;
  display: inline-block;
  height: 10px;
  width: 10px;
`;

// Drawn in CSS rather than using the plate SVG: that art has grey gradients
// baked in, so it cannot take the rating's tier colour.
export const PremierPlate = styled.span<{ $color: string }>`
  align-items: center;
  display: inline-flex;

  i {
    background: ${({ $color }) => $color};
    height: 20px;
    transform: skewX(-14deg);
    width: 3px;
  }

  // Both bars and the band carry the same 14deg skew, so these gaps are just
  // the margins. The pair reads as one mark, sitting right up against the band.
  i:first-child {
    margin-right: 2px;
  }

  i:nth-child(2) {
    margin-right: 2px;
  }

  // Dark plate carrying the rating in the tier colour, matching the bars beside
  // it, rather than light ink on a bright band.
  span {
    background: ${({ $color, theme }) =>
      `linear-gradient(105deg, ${rgba($color, 0.28)} 0%, ${rgba(
        $color,
        0.14,
      )} 100%), ${theme.colors.midnight}`};
    border: 1px solid ${({ $color }) => rgba($color, 0.55)};
    border-radius: 2px;
    padding: 2px 7px;
    transform: skewX(-14deg);
  }

  em {
    color: ${({ $color }) => $color};
    display: block;
    font-size: 14px;
    font-style: normal;
    font-weight: 800;
    letter-spacing: 0.01em;
    transform: skewX(14deg);
  }
`;
export const RankArrow = styled.span<{ $promoted: boolean }>`
  align-items: center;
  color: ${({ $promoted, theme }) =>
    $promoted ? theme.colors.envy : theme.colors.alert};
  display: inline-flex;
  flex-shrink: 0;
  font-size: 14px;
`;

// A true minus sign rather than a hyphen, and tabular figures, so a column of
// these lines up instead of jittering by a pixel per row.
export const RankDelta = styled.span<{ $promoted: boolean }>`
  color: ${({ theme }) => theme.colors.ice};
  flex-shrink: 0;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
`;

export const MatchTitle = styled.h1`
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin: 0;
  white-space: nowrap;
`;

// One centred column: outcome, score, when. Team names live in the scoreboard
// headers, which is also what removes the old three-across row that could not
// stay symmetric once it was allowed to wrap.
export const BannerCenter = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: center;
`;

export const ResultBadge = styled.span<{ $won?: boolean }>`
  background: ${({ $won, theme }) =>
    $won === undefined
      ? theme.colors.chalkAlpha20
      : $won
      ? theme.colors.envyAlpha20
      : theme.colors.alertAlpha20};
  border-radius: 999px;
  color: ${({ $won, theme }) =>
    $won === undefined
      ? theme.colors.chalk
      : $won
      ? theme.colors.envy
      : theme.colors.alert};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  padding: 3px 10px;
  text-transform: uppercase;
`;

export const Score = styled.div<{ $result?: string | null }>`
  align-items: baseline;
  text-shadow:
    0 1px 4px ${({ theme }) => theme.colors.midnight},
    0 2px 12px ${({ theme }) => theme.colors.midnightAlpha80};
  display: flex;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  gap: 8px;
  justify-content: center;
  line-height: 1;

  b {
    // Three way: an unreported result is not a loss, and used to render as one.
    color: ${({ $result, theme }) =>
      !$result
        ? theme.colors.ice
        : $result === "Win"
        ? theme.colors.envy
        : theme.colors.alert};
    font-size: 36px;
  }

  u {
    color: ${({ theme }) => theme.colors.ice};
    font-size: 36px;
    text-decoration: none;
  }

  i {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
  }
`;

export const TeamBlock = styled.div`
  margin-bottom: 20px;

  // A grid item is its own formatting context, so this margin cannot collapse
  // out of the column: left on, it padded the scoreboard's measured height by
  // 20px and the highlights card beside it hung that far below the last table.
  &:last-child {
    margin-bottom: 0;
  }
`;

// Only earns its place where columns are actually hidden, so it stays out of
// the way at widths where the whole table fits.
export const MoreStatsToggle = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  margin-top: 8px;
  padding: 7px 12px;
  width: 100%;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    display: block;
  }
`;

// The accent bar reads win or loss at a glance. The team label lives in the
// header row's first cell rather than floating on
// its own line above the table. Stays display:table-cell so the column keeps
// its place in the table layout.
export const TeamHeadCell = styled.th<{ $won?: boolean }>`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 15px;
  font-weight: 700;

  i {
    background: ${({ $won, theme }) =>
      $won === undefined
        ? theme.colors.chalkAlpha40
        : $won
        ? theme.colors.envy
        : theme.colors.alert};
    border-radius: 2px;
    display: inline-block;
    height: 14px;
    margin-right: 8px;
    vertical-align: -2px;
    width: 4px;
  }
`;
export const PlayerCell = styled.span`
  align-items: center;
  display: flex;
  font-size: 17px;
  font-weight: 700;
  gap: 10px;
`;

// Name leads, rating sits under it. Inline, the plate pushed every name to a
// different x position and the column stopped scanning as a column.
export const PlayerText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

// The avatar points at the same place as the name. It is aria-hidden and out
// of the tab order so keyboard and screen reader users get one stop for the
// player, not two identical ones.
export const AvatarLink = styled(Link)`
  border-radius: 50%;
  display: inline-flex;
  flex-shrink: 0;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.85;
  }
`;

// A real anchor, so the row can be opened in a new tab the way a name that
// looks like a link ought to be.
export const PlayerLink = styled(Link)`
  align-items: center;
  color: inherit;
  display: inline-flex;
  gap: 6px;
  min-width: 0;
  text-decoration: none;

  &:hover span[data-name],
  &:focus-visible span[data-name] {
    color: ${({ theme }) => theme.colors.envy};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 3px;
  }

  // Capped: this column was sizing itself to the longest name in the lobby,
  // which set the whole table's minimum width and pushed the two column
  // breakpoint up past common laptops. The full name stays in the title.
  span[data-name] {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: color 0.15s ease;
    white-space: nowrap;
  }
`;

// Marks the players who actually have an Allstar account behind the name.
export const AllstarMark = styled(AMark)`
  flex-shrink: 0;
  height: 13px;
  width: 13px;
`;

export const Avatar = styled.img`
  border-radius: 50%;
  flex-shrink: 0;
  height: 32px;
  object-fit: cover;
  width: 32px;
`;

export const AvatarFallback = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 50%;
  display: inline-flex;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
  height: 32px;
  justify-content: center;
  width: 32px;
`;

// The map art is decorative, so it sits under a heavy scrim rather than
// fighting the text for contrast.
export const Hero = styled.header<{ $art?: string }>`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.midnight};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  margin-bottom: 16px;
  // border-box or min-height is content height and the padding stacks on top:
  // that is why this measured 212px when it was set to 150px.
  box-sizing: border-box;
  // Was 210px for a block carrying a score and two team names. It now carries
  // the outcome and the page's tabs in two thirds of that.
  min-height: 140px;
  overflow: hidden;
  // Bottom padding is the tab strip's lane.
  padding: 14px 20px 44px;
  position: relative;

  // Blur lives on a pseudo element because a background-image can't take a
  // filter. Inset overscan keeps the blur from fading out at the edges.
  &::before {
    background: ${({ $art }) => ($art ? `url(${$art})` : "none")} center / cover
      no-repeat;
    content: "";
    filter: blur(5px);
    inset: -20px;
    position: absolute;
  }

  // Two scrims: one keeps the text side readable, one fades the art out at the
  // bottom and right so the panel melts into the page instead of ending on a
  // hard edge.
  // Light scrim only: legibility comes from the text-shadow below, so the map
  // art stays visible instead of being crushed flat.
  &::after {
    background:
      // Pool of shade under the score itself. A flat scrim heavy enough to
      // carry 36px glyphs over blurred art would have crushed the whole image.
      radial-gradient(
        ellipse 42% 90% at 50% 46%,
        ${({ theme }) => theme.colors.midnightAlpha80} 0%,
        transparent 100%
      ),
      linear-gradient(
        90deg,
        ${({ theme }) => theme.colors.midnightAlpha80} 0%,
        ${({ theme }) => theme.colors.midnightAlpha60} 25%,
        ${({ theme }) => theme.colors.midnightAlpha60} 75%,
        ${({ theme }) => theme.colors.midnightAlpha80} 100%
      ),
      linear-gradient(
        180deg,
        ${({ theme }) => theme.colors.midnightAlpha30} 0%,
        transparent 50%,
        ${({ theme }) => theme.colors.midnightAlpha50} 100%
      );
    content: "";
    inset: 0;
    position: absolute;
  }

  > * {
    position: relative;
    text-shadow: 0 1px 4px ${({ theme }) => theme.colors.midnightAlpha80};
    z-index: 1;
  }
`;
export const HeroInfo = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  min-width: 0;
`;

export const MapBadge = styled.img`
  height: 32px;
  width: 32px;
`;

// Allowed to wrap: nowrap here was clipping ~20px of the header on a phone.
export const HeroMeta = styled.span`
  color: ${({ theme }) => theme.colors.iceAlpha75};
  font-size: 14px;
  font-weight: 600;
`;

// Floated over the art rather than stacked, so the score block centres in the
// full height instead of being pushed below this row.
// Map identity sits in the corner so the outcome can own the centre. Goes
// static on narrow widths, where there is no room to float anything.
export const HeroTop = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  left: 20px;
  position: absolute;
  right: 20px;
  top: 14px;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    position: static;
    width: 100%;
  }
`;

export const HeroIdentity = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  min-width: 0;
`;

// The date and the share control travel together on the right, so HeroTop keeps
// two children and its space-between still pins identity left and this right.
export const HeroActions = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 12px;
`;

// It sits on photographic hero art, so it carries its own backing rather than
// being a ghost button over an unpredictable image.
export const ShareButton = styled.button<{ $copied?: boolean }>`
  align-items: center;
  background: ${({ theme }) => theme.colors.midnightAlpha60};
  border: 1px solid
    ${({ theme, $copied }) =>
      $copied ? theme.colors.envyAlpha40 : theme.colors.chalkAlpha20};
  border-radius: 999px;
  color: ${({ theme, $copied }) =>
    $copied ? theme.colors.envy : theme.colors.iceAlpha75};
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  gap: 6px;
  padding: 6px 12px;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.midnightAlpha80};
    color: ${({ theme, $copied }) =>
      $copied ? theme.colors.envy : theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  svg {
    flex-shrink: 0;
  }
`;

export const ModeLabel = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
`;

export const Highlights = styled.section`
  margin-bottom: 24px;

  @media (min-width: 1300px) {
    display: flex;
    flex-direction: column;
    flex: 1;
    margin-bottom: 0;
    min-height: 0;
  }
`;

// The card holds only the list. Its heading and sort sit outside it, so the
// card reads as the container for the highlights rather than a titled panel.
export const HighlightsCardWrap = styled.div`
  @media (min-width: 1300px) {
    flex: 1;
    // A floor, so a two-player scoreboard cannot squash the list to nothing.
    min-height: 320px;
    position: relative;
  }
`;

export const HighlightsCard = styled.div`
  background: ${({ theme }) => theme.colors.navy200};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 10px;
  padding: 8px;

  // Absolute inside the wrapper so the list's own length never inflates the
  // grid row: the row is sized by the scoreboard, and this scrolls to fit.
  @media (min-width: 1300px) {
    inset: 0;
    overflow-y: auto;
    position: absolute;
    scrollbar-color: ${({ theme }) => theme.colors.navy50} transparent;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.colors.navy50};
      border-radius: 3px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }
`;

export const HighlightsHead = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  justify-content: space-between;
  margin-bottom: 12px;
`;

export const HighlightsTitle = styled.h2`
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0;

  // How many there are in total, next to the name rather than in the status
  // line, which counts states rather than the set.
  b {
    color: ${({ theme }) => theme.colors.chalk};
    font-weight: 700;
    margin-left: 4px;
  }
`;

// Map art stands in until the real thumbnail exists, dimmed hard so it never
// reads as the finished clip.
// Ice glyph on envy, matching the Clip pill. A dark triangle on the same green
// made the two calls to action look like two different systems.
export const PlayBadge = styled.span<{ $small?: boolean }>`
  background: ${({ theme }) => theme.colors.envy};
  border: 0;
  border-radius: 50%;
  box-shadow: 0 2px 10px ${({ theme }) => theme.colors.midnightAlpha60};
  height: ${({ $small }) => ($small ? "22px" : "44px")};
  position: relative;
  transition: filter 0.15s ease;
  width: ${({ $small }) => ($small ? "22px" : "44px")};

  &::after {
    border-bottom: ${({ $small }) => ($small ? "4px" : "7px")} solid transparent;
    border-left: ${({ $small }) => ($small ? "7px" : "12px")} solid
      ${({ theme }) => theme.colors.ice};
    border-top: ${({ $small }) => ($small ? "4px" : "7px")} solid transparent;
    content: "";
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-40%, -50%);
  }
`;

// Two columns wherever there is genuinely room. The app's fixed 240px rail
// means the content box is viewport - 304. With the name column capped the
// table's natural minimum is ~709, so 709 + 20 gap + 260 column + 304 = 1293.
// The clips column is elastic so it takes the slack on wider screens rather
// than forcing an even higher threshold.
export const OverviewGrid = styled.div`
  display: grid;
  gap: 20px;

  // No align-items: start. The columns stretch to a common row height, which
  // is what lets the highlights card match the scoreboard beside it.
  @media (min-width: 1300px) {
    // The clips column takes some of the extra width the wider page allows,
    // rather than every pixel going to the scoreboard and lengthening its rows.
    grid-template-columns: minmax(0, 1fr) minmax(280px, 400px);
  }
`;

// Source order puts clips first so they lead once the layout stacks; explicit
// placement moves them right when there are two columns.
export const ClipsColumn = styled.div`
  min-width: 0;

  @media (min-width: 1300px) {
    display: flex;
    flex-direction: column;
    grid-column: 2;
    grid-row: 1;
  }
`;

export const BoardColumn = styled.div`
  min-width: 0;

  @media (min-width: 1300px) {
    grid-column: 1;
    grid-row: 1;
  }
`;

// No bottom margin: this used to sit directly above the first card and needed
// clearance. It is a flex child of HighlightsHead now, which owns the spacing.
export const SortGroup = styled.div`
  align-items: center;
  display: flex;
  gap: 2px;
`;

export const SortLabel = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 600;
  margin-right: 4px;
`;

export const SortOption = styled.button<{ $active?: boolean }>`
  background: ${({ $active, theme }) =>
    $active ? theme.colors.chalkAlpha10 : "transparent"};
  border: 0;
  border-radius: 6px;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.ice : theme.colors.chalk};
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 9px;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

// The featured clip keeps the poster treatment; it is what stops the column
// reading as a sidebar.
export const FeaturedCard = styled.article`
  display: block;
  margin-bottom: 12px;
`;

export const CompactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

// Roughly 64px tall, so six to eight are on screen at once. A ranking nobody
// can see the shape of is not a ranking.
export const CompactItem = styled.button`
  align-items: center;
  // Up to 40 of these exist and about six are ever on screen. Skipping layout
  // and paint for the rest is free; the intrinsic size keeps the scrollbar
  // honest so the track does not jump as rows come into view.
  content-visibility: auto;
  contain-intrinsic-size: auto 64px;
  background: none;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  gap: 10px;
  padding: 5px;
  text-align: left;
  transition: background 0.15s ease;
  width: 100%;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.chalkAlpha06};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: -2px;
  }

  &:disabled {
    cursor: default;
  }

  &:hover:not(:disabled) span[data-cta] {
    filter: brightness(1.1);
  }
`;

export const CompactThumb = styled.div<{ $image?: string; $dim?: boolean }>`
  align-items: center;
  background: ${({ $image }) =>
      $image ? `url(${$image}) center / cover no-repeat` : "none"},
    ${({ theme }) => theme.colors.midnightAlpha80};
  border-radius: ${COMPACT_RADIUS};
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha10};
  box-sizing: border-box;
  display: flex;
  flex-shrink: 0;
  height: 54px;
  justify-content: center;
  // Same single clip as CardThumb, for the same measured reason.
  clip-path: inset(0 round ${COMPACT_RADIUS});
  position: relative;
  width: 96px;

  &::after {
    backdrop-filter: ${({ $dim }) => ($dim ? "blur(6px)" : "none")};
    -webkit-backdrop-filter: ${({ $dim }) => ($dim ? "blur(6px)" : "none")};
    background: ${({ theme, $dim }) =>
      $dim ? theme.colors.midnightAlpha60 : theme.colors.midnightAlpha30};
    content: "";
    inset: 0;
    position: absolute;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

export const CompactBody = styled.span`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

export const CompactTitle = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 13px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Centred on the thumbnail, the same place the featured card puts its call to
// action. A span, not a button: the row is already the control, and a button
// inside a button is invalid markup.
export const CompactCta = styled.span`
  background: ${({ theme }) => theme.colors.envy};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 11px;
  font-weight: 800;
  left: 50%;
  padding: 4px 10px;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: filter 0.15s ease;
  white-space: nowrap;
  z-index: 2;
`;

export const CompactMeta = styled.span<{
  $state: "ready" | "rendering" | "failed" | "idle";
}>`
  align-items: center;
  color: ${({ $state, theme }) =>
    $state === "rendering"
      ? theme.colors.envy
      : $state === "failed"
      ? theme.colors.alert
      : theme.colors.chalk};
  display: flex;
  font-size: 11px;
  font-weight: 600;
  gap: 5px;

  img {
    height: 12px;
    width: auto;
  }
`;

export const CardAction = styled.button`
  background: none;
  border: 0;
  cursor: pointer;
  display: block;
  padding: 0;
  position: relative;
  width: 100%;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
  }

  &:hover:not(:disabled) span[data-cta] {
    filter: brightness(1.1);
  }
`;

export const CardThumb = styled.div<{
  $image?: string;
  $dim?: boolean;
}>`
  align-items: center;
  // Visual only: the frame's own button takes the click, so nothing here can
  // intercept it or steal the hit area from the share control above it.
  pointer-events: none;
  aspect-ratio: 16 / 9;
  background: ${({ $image }) =>
      $image ? `url(${$image}) center / cover no-repeat` : "none"},
    ${({ theme }) => theme.colors.midnightAlpha80};
  // An inset ring rather than a border: a real border sat outside the button's
  // width (2px overhang) and left a pale hairline on the rounded corners.
  border: 0;
  border-radius: ${THUMB_RADIUS};
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha10};
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  // One clip for the whole stack. overflow + radius left two anti-aliased
  // curves on the same edge (this box and each rounded child), and the footage
  // leaked through as a light rim on the corners. clip-path clips every
  // descendant in one pass, including the blurred backdrop layer, so no child
  // needs a radius of its own. Measured, not guessed: the corner halo went to 0.
  clip-path: inset(0 round ${THUMB_RADIUS});
  position: relative;
  width: 100%;

  // A preview is stock footage: often the wrong weapon, never this moment. It
  // is blurred hard so it reads as texture rather than as a claim about what
  // happened. A real rendered frame only needs holding back behind its badge.
  &::after {
    backdrop-filter: ${({ $dim }) => ($dim ? "blur(9px)" : "none")};
    -webkit-backdrop-filter: ${({ $dim }) => ($dim ? "blur(9px)" : "none")};
    background: ${({ theme, $dim }) =>
      $dim ? theme.colors.midnightAlpha60 : theme.colors.midnightAlpha30};
    content: "";
    inset: 0;
    position: absolute;
  }

  em {
    background: ${({ theme }) => theme.colors.midnightAlpha80};
    border-radius: 4px;
    bottom: 6px;
    font-size: 11px;
    font-style: normal;
    font-weight: 700;
    position: absolute;
    padding: 2px 6px;
    right: 6px;
    z-index: 2;
  }
`;

// One centred affordance per card, whatever state it is in.
export const CardCta = styled.span<{ $variant: "play" | "create" | "busy" }>`
  align-items: center;
  background: ${({ $variant, theme }) =>
    $variant === "create" ? theme.colors.envy : "transparent"};
  border: 0;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.ice};
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  gap: 6px;
  justify-content: center;
  left: 50%;
  padding: ${({ $variant }) => ($variant === "create" ? "8px 16px" : "0")};
  position: absolute;
  top: 42%;
  transform: translate(-50%, -50%);
  transition:
    transform 0.15s ease,
    background 0.15s ease;
  white-space: nowrap;
  z-index: 2;
`;

// Title and weapon sit on the frame rather than under it, so the card is the
// thumbnail and nothing else.
export const CardOverlay = styled.div`
  background: linear-gradient(
    180deg,
    transparent 0%,
    ${({ theme }) => theme.colors.midnightAlpha80} 60%
  );
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  left: 0;
  padding: 22px 10px 9px;
  position: absolute;
  right: 0;
  text-align: left;
  z-index: 2;
`;

export const CardTitle = styled.h3`
  color: ${({ theme }) => theme.colors.ice};
  display: -webkit-box;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0;
  overflow: hidden;
  text-shadow: 0 1px 3px ${({ theme }) => theme.colors.midnightAlpha80};
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

export const CardMeta = styled.p<{
  $state: "ready" | "rendering" | "failed" | "idle";
}>`
  align-items: center;
  color: ${({ $state, theme }) =>
    $state === "rendering"
      ? theme.colors.envy
      : $state === "failed"
      ? theme.colors.alert
      : theme.colors.chalk};
  display: flex;
  font-size: 11px;
  font-weight: 600;
  gap: 6px;
  margin: 0;
  text-shadow: 0 1px 3px ${({ theme }) => theme.colors.midnightAlpha80};

  img {
    height: 13px;
    width: auto;
  }
`;

export const EmptyState = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  padding: 28px 20px;
  text-align: center;
`;

// Lives on the page now rather than inside the header, so it needs no plate.
export const Spinner = styled.span`
  animation: matchClipSpin 0.9s linear infinite;
  border: 2px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 50%;
  border-top-color: ${({ theme }) => theme.colors.envy};
  height: 22px;
  width: 22px;

  @keyframes matchClipSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const MomentRowWrap = styled.div`
  align-items: center;
  display: flex;
  gap: 3px;
  margin-top: 6px;
`;

export const MomentIcon = styled.img`
  height: 16px;
  margin-right: 3px;
  width: auto;
`;

export const VictimAvatar = styled.img`
  border-radius: 50%;
  height: 18px;
  object-fit: cover;
  width: 18px;
`;

// Two tabs stretched across 1440px read as a segmented control, not navigation.
// One rule running the full width of the banner, with only the active tab's
// segment lit. The shared Tabs component keeps its analytics and ?tab= syncing;
// only its presentation is overridden here.
export const TabsWrap = styled.div`
  // Pinned rather than laid out: as a flex child it competed with the centred
  // score block and pulled it off centre.
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;

  // The continuous line. Each tab draws its own 2px border on top of this, so
  // the active one lights its segment and the rest merge into the rule.
  border-bottom: 2px solid ${({ theme }) => theme.colors.navy};

  // Five tabs do not fit a phone. Scroll the strip rather than letting it push
  // the hero wider, and hide the bar because the rule underneath is the edge.
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > div {
    gap: 28px;
    padding-left: 20px;
    padding-right: 20px;
    width: max-content;
    min-width: 100%;
  }

  // Rebass stretches each tab with flex: 1 1 0; these should hug their labels.
  > div > * {
    flex: 0 0 auto;
    margin-bottom: -2px;
    padding-bottom: 11px;
  }

  > div > *:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

export const DuelList = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
`;

export const DuelRow = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy200};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 8px;
  display: flex;
  gap: 12px;
  padding: 12px 14px;
`;

export const DuelName = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 700;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DuelTally = styled.span<{ $won: boolean }>`
  color: ${({ $won, theme }) =>
    $won ? theme.colors.envy : theme.colors.alert};
  flex-shrink: 0;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  font-weight: 800;

  small {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 15px;
    font-weight: 700;
    margin: 0 2px;
  }
`;

// Share of the duel won, read left to right. Flat bar, no gradient: it is a
// ratio, not a gauge.
export const DuelBar = styled.span`
  background: ${({ theme }) => theme.colors.alert};
  border-radius: 999px;
  flex: 0 0 84px;
  height: 5px;
  overflow: hidden;
  position: relative;
`;

export const DuelBarFill = styled.span<{ $pct: number }>`
  background: ${({ theme }) => theme.colors.envy};
  inset: 0 auto 0 0;
  position: absolute;
  width: ${({ $pct }) => $pct}%;
`;

// The cards carry their own call to action on the thumbnail now, so this is
// just the button an empty state needs to offer a way forward.
export const GhostButton = styled.button`
  align-self: center;
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha40};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  margin-top: 12px;
  padding: 8px 14px;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.envyAlpha10};
    border-color: ${({ theme }) => theme.colors.envy};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

// Sorting lives on the header itself; a separate control row would be a second
// thing to find for something the column already names.
export const SortHeader = styled.button<{ $active?: boolean }>`
  background: none;
  border: 0;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.ice : theme.colors.chalk};
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  padding: 0;
  text-transform: uppercase;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 3px;
  }

  span {
    color: ${({ theme }) => theme.colors.envy};
    margin-left: 4px;
  }
`;

// Green when they were up on the trade, red when they were well down, yellow
// in the dip before that, plain when it was even.
// One restrained scale across every metric. A scoreboard is read, not alarmed
// at: standing out is green, ordinary is plain, behind is simply muted. Nothing
// here is an error, so nothing here is red.
// Two greens, deliberately unequal: envy is scarce and means the match's best,
// lime means good. One green for both made a solid game and a carry look the
// same at a glance, which is the one thing a scoreboard exists to separate.
export const TONE_COLOR = {
  elite: "envy",
  strong: "lime",
  even: "ice",
  weak: "chalk",
  poor: "caution",
} as const;

export type TStatTone = keyof typeof TONE_COLOR;

export const StatValue = styled.span<{
  $tone: TStatTone;
}>`
  color: ${({ $tone, theme }) => theme.colors[TONE_COLOR[$tone]]};
  font-variant-numeric: tabular-nums;
  font-weight: 700;

  small {
    color: ${({ theme }) => theme.colors.chalkAlpha40};
    font-size: inherit;
    font-weight: 600;
    margin: 0 3px;
  }
`;

// The VOD is the biggest artifact this match produced, so its panel borrows the
// banner's own atmosphere rather than sitting in a dashed placeholder box. One
// stage serves every state, so the tab never reads as scaffolding.
export const VodStage = styled.section<{ $art?: string }>`
  align-items: flex-start;
  background-color: ${({ theme }) => theme.colors.midnight};
  border-radius: 10px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 340px;
  overflow: hidden;
  padding: 28px;
  position: relative;

  // Same technique the banner uses: blur on a pseudo element, overscanned so it
  // does not fade at the edges.
  &::before {
    background: ${({ $art }) => ($art ? `url(${$art})` : "none")} center / cover
      no-repeat;
    content: "";
    filter: blur(3px);
    inset: -20px;
    position: absolute;
  }

  &::after {
    background: radial-gradient(
        ellipse 70% 90% at 20% 100%,
        ${({ theme }) => theme.colors.midnightAlpha80} 0%,
        transparent 100%
      ),
      linear-gradient(
        180deg,
        ${({ theme }) => theme.colors.midnightAlpha50} 0%,
        ${({ theme }) => theme.colors.midnightAlpha80} 100%
      );
    content: "";
    inset: 0;
    position: absolute;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    min-height: 300px;
    padding: 20px;
  }
`;

// The house display voice, the one the nav headlines use. The panel was opting
// out of it with an 18px title.
export const VodHeadline = styled.h2`
  color: ${({ theme }) => theme.colors.ice};
  font-size: clamp(28px, 4vw, 44px);
  font-style: italic;
  font-weight: 900;
  letter-spacing: -0.01em;
  line-height: 1.02;
  margin: 0 0 10px;
  max-width: 15ch;
  text-shadow: 0 2px 12px ${({ theme }) => theme.colors.midnightAlpha80};
  text-transform: uppercase;
`;

export const VodLede = styled.p`
  // Solid, not iceAlpha75: translucent body text over artwork cannot hold a
  // contrast floor no matter how heavy the scrim behind it gets. Hierarchy
  // comes from size against a 44px display line instead.
  color: ${({ theme }) => theme.colors.ice};
  font-size: 15px;
  line-height: 1.5;
  margin: 0 0 18px;
  max-width: 46ch;
  text-shadow: 0 1px 6px ${({ theme }) => theme.colors.midnightAlpha80};
`;

// One tick per round, straight off the VOD's own markers, lit where the round
// produced a moment. It says "a full match, scrubbable, and these bits are
// yours" without a word of copy.
export const RoundRibbon = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 3px;
  height: 32px;
  margin-bottom: 16px;
  max-width: 520px;
  width: 100%;
`;

export const RoundTick = styled.span<{ $hot?: boolean; $muted?: boolean }>`
  background: ${({ $hot, $muted, theme }) =>
    $muted
      ? theme.colors.chalkAlpha20
      : $hot
      ? theme.colors.envy
      : theme.colors.chalkAlpha40};
  border-radius: 2px;
  flex: 1 1 0;
  height: ${({ $hot }) => ($hot ? "100%" : "52%")};
  min-width: 2px;
  transform-origin: bottom;

  @media (prefers-reduced-motion: no-preference) {
    animation: vodTickIn 420ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  @keyframes vodTickIn {
    from {
      transform: scaleY(0.35);
    }
    to {
      transform: scaleY(1);
    }
  }
`;

export const VodFooter = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 18px;
`;

// The same envy pill the highlight cards use for their call to action.
export const VodCta = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.envy};
  border: 0;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  gap: 8px;
  padding: 11px 22px;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(1.1);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.ice};
    outline-offset: 3px;
  }
`;

export const VodNote = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  gap: 6px;
  text-shadow: 0 1px 6px ${({ theme }) => theme.colors.midnightAlpha80};
`;

// The matchup that decided the match, given the room to say so. Everything
// else on this tab is a ledger; this is the one block with a voice.
export const Nemesis = styled.section`
  background: ${({ theme }) => theme.colors.navy200};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 10px;
  margin-bottom: 20px;
  overflow: hidden;
`;

export const NemesisHead = styled.div`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  display: flex;
  flex-wrap: wrap;
  gap: 14px 18px;
  padding: 16px 18px;
`;

export const NemesisWho = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

// Sits under the name, never above it: a label stacked over a heading is an
// eyebrow, and the heading should carry its own weight.
export const NemesisSub = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  font-weight: 600;
`;

export const NemesisName = styled.h2`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Their number leads, because on this block they are the subject.
// The leading numeral is whichever side the card is about: your wins on the
// best duel, their kills on the nemesis. Colour follows that, so the two cards
// read as opposites at a glance instead of as two identical scorelines.
export const NemesisScore = styled.div<{ $tone?: "good" | "bad" }>`
  align-items: baseline;
  display: flex;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  gap: 6px;
  margin-left: auto;

  b {
    color: ${({ $tone, theme }) =>
      $tone === "good" ? theme.colors.envy : theme.colors.alert};
    font-size: 30px;
  }

  u {
    color: ${({ theme }) => theme.colors.ice};
    font-size: 30px;
    text-decoration: none;
  }

  i {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
  }
`;

// What actually happened, one line per kill. No clip exists for another
// player's moment, so this is a record rather than a reel.
export const KillList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const KillRow = styled.li`
  align-items: center;
  display: flex;
  gap: 12px;
  padding: 10px 18px;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  }

  img {
    flex-shrink: 0;
    height: 16px;
    width: auto;
  }
`;

export const KillWhat = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const KillRound = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  flex-shrink: 0;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  margin-left: auto;
`;

// Ledger rows stay quiet until asked. The whole row is the control.
export const LedgerRow = styled.div`
  background: ${({ theme }) => theme.colors.navy200};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
`;

export const LedgerToggle = styled.button<{ $open?: boolean }>`
  align-items: center;
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: flex;
  font-family: inherit;
  gap: 12px;
  padding: 12px 14px;
  text-align: left;
  width: 100%;

  // Ice rather than chalk: a grey wash over a green or red tint muddied it,
  // where a light one lifts whichever tint is underneath.
  &:hover {
    background: ${({ theme }) => theme.colors.iceAlpha08};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: -2px;
  }

  svg {
    color: ${({ theme }) => theme.colors.chalk};
    flex-shrink: 0;
    transition: transform 0.15s ease;
    transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
  }
`;

export const LedgerPanel = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
`;

// Names what the list is, so "their moments" is never mistaken for "all of
// their moments".
export const KillHeading = styled.p`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  font-weight: 600;
  margin: 0;
  // Not uppercased: the sentence carries a player's name, and usernames are
  // cased the way their owner cased them.
  padding: 12px 18px 4px;
`;

export const KillAction = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha40};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  flex-shrink: 0;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 10px;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.envyAlpha10};
    border-color: ${({ theme }) => theme.colors.envy};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.chalk};
    cursor: default;
  }
`;

// --- Pre-content states -----------------------------------------------------
// Loading, load-failed, and no-match all used to collapse the page into a
// centred dashed box. These keep the page's own type and spacing instead.

export const StateWrap = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  // Not centred in the viewport: sitting a little high reads as a state the
  // page is in, rather than a screen of its own.
  margin: 8vh auto 0;
  // A measure, not a width. Translated copy grows into it instead of past it.
  max-width: 46ch;
  text-align: center;
`;

export const StateIcon = styled.span<{ $tone?: "alert" | "quiet" }>`
  align-items: center;
  border: 1px solid
    ${({ $tone, theme }) =>
      $tone === "alert"
        ? theme.colors.alertAlpha80
        : theme.colors.chalkAlpha20};
  border-radius: 50%;
  color: ${({ $tone, theme }) =>
    $tone === "alert" ? theme.colors.alert : theme.colors.chalk};
  display: inline-flex;
  height: 44px;
  justify-content: center;
  margin-bottom: 18px;
  width: 44px;

  svg {
    height: 22px;
    width: 22px;
  }
`;

export const StateTitle = styled.h1`
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin: 0 0 8px;
`;

export const StateBody = styled.p`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`;

export const StateActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
`;

// The filled sibling of GhostButton, for the one action worth taking first.
export const PrimaryButton = styled.button`
  background: ${({ theme }) => theme.colors.envy};
  border: 1px solid ${({ theme }) => theme.colors.envy};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.midnight};
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  padding: 8px 16px;
  transition:
    filter 0.15s ease,
    opacity 0.15s ease;

  &:hover:not(:disabled) {
    filter: brightness(1.08);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

// GhostButton centres and top-margins itself for its use inside EmptyState;
// in an actions row the row owns the spacing.
export const StateGhostButton = styled(GhostButton)`
  align-self: auto;
  margin-top: 0;
`;

export const VisuallyHidden = styled.span`
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

// --- Loading skeleton -------------------------------------------------------
// The real layout components carry the geometry; these only fill them. Anything
// here that duplicated a width would drift the first time the page changed.

export const SkeletonScope = styled.div`
  // react-loading-skeleton sets its vars on the skeleton element itself, so the
  // override has to reach the element rather than inherit down to it.
  .react-loading-skeleton {
    --base-color: ${({ theme }) => theme.colors.navy100};
    --highlight-color: ${({ theme }) => theme.colors.navy50};
  }

  @media (prefers-reduced-motion: reduce) {
    .react-loading-skeleton {
      --pseudo-element-display: none;
    }
  }
`;

export const SkeletonHeroIdentity = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
`;

// Bare on purpose: TabsWrap already styles its own child div (gap, left inset)
// and that child's children (the 11px baseline padding). Restating any of it
// here would double the strip's height and overflow the hero's tab lane.
export const SkeletonTabs = styled.div`
  display: flex;
`;

// The compact moment row: 96x54 thumb, 10px gap, 5px padding.
export const SkeletonMomentRow = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  padding: 5px;
`;

export const SkeletonMomentBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const SkeletonTableRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  padding: 11px 14px;
`;

// --- Duel stat comparison ---------------------------------------------------
// Same device as DuelBar: a track whose fill is your share and whose ground is
// theirs. Reusing it means a duel's kill count and a duel's stat line are read
// the same way, rather than being two charts that happen to sit together.

export const CompareBlock = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 12px;
`;

export const CompareHead = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  display: grid;
  font-size: 11px;
  font-weight: 700;
  gap: 10px;
  grid-template-columns: 58px 1fr 58px;
  letter-spacing: 0.06em;
  text-transform: uppercase;

  // The opponent's name can be long; it gets the ellipsis, not the layout.
  > :last-child {
    overflow: hidden;
    text-align: right;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const CompareRow = styled.div`
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: 58px 1fr 58px;
`;

export const CompareValue = styled.span<{
  $side: "mine" | "theirs";
  $lead: boolean;
}>`
  color: ${({ $lead, $side, theme }) =>
    // eslint-disable-next-line no-nested-ternary
    !$lead
      ? theme.colors.chalk
      : $side === "mine"
      ? theme.colors.envy
      : theme.colors.ice};
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  text-align: ${({ $side }) => ($side === "mine" ? "left" : "right")};
`;

export const CompareMid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const CompareLabel = styled.span`
  color: ${({ theme }) => theme.colors.chalkAlpha80};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-align: center;
  text-transform: uppercase;
`;

// A centre line is parity. The bar leaves it toward whoever led, and its length
// is how far ahead they were. The old version filled from the left by share of
// total, which for a rating of 1.38 against 1.04 drew a 57% bar: a number that
// means nothing, because share of total is not a thing a ratio has.
export const CompareTrack = styled.span`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 999px;
  display: block;
  height: 6px;
  position: relative;

  // The parity tick, so an even stat still reads as deliberately even.
  &::after {
    background: ${({ theme }) => theme.colors.chalkAlpha40};
    content: "";
    inset: -2px auto -2px calc(50% - 1px);
    position: absolute;
    width: 2px;
  }
`;

// No red anywhere in here: you are envy, they are plain ice. One hue plus one
// neutral says "you against them" without spending the alarm colour on a
// teammate-shaped fact, and it is what stopped the panel reading as a warning.
export const CompareFill = styled.span<{
  $pct: number;
  $side: "mine" | "theirs";
}>`
  background: ${({ $side, theme }) =>
    $side === "mine" ? theme.colors.envy : theme.colors.ice};
  border-radius: 999px;
  bottom: 0;
  position: absolute;
  top: 0;
  // Half the track wide, scaled from the centre edge outward, so the gap
  // animates on the compositor instead of relaying out the track.
  width: 50%;
  transform: scaleX(${({ $pct }) => $pct / 100});
  transition: transform 0.3s ease;
  ${({ $side }) =>
    $side === "mine"
      ? "right: 50%; transform-origin: right center;"
      : "left: 50%; transform-origin: left center;"}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// The pair at the top of the tab. Two cards, not one: the match's best duel and
// its worst are different facts, and showing only the nemesis taught players
// nothing about where they actually won.
export const BookendGrid = styled.div`
  display: grid;
  gap: 12px;
  margin-bottom: 16px;

  @media (min-width: 860px) {
    grid-template-columns: 1fr 1fr;
  }

  // The card owns its spacing when it stands alone; in the pair the grid does.
  > section {
    margin-bottom: 0;
  }
`;

// Beside the name, never stacked above it: the file already settled that a
// label over a heading is an eyebrow. A tinted badge alongside reads as a
// classification instead, the same way ResultBadge does.
export const BookendTag = styled.span<{ $good: boolean }>`
  background: ${({ $good, theme }) =>
    $good ? theme.colors.limeAlpha20 : theme.colors.alertAlpha20};
  border-radius: 999px;
  color: ${({ $good, theme }) =>
    $good ? theme.colors.lime : theme.colors.alert};
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  padding: 3px 8px;
  text-transform: uppercase;
`;

export const BookendTitleRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`;

// --- Your Performance -------------------------------------------------------
// The tab's thesis is position, not magnitude: every number here is read
// against the other nine players in the lobby, which the scoreboard cannot do
// for one player without making you scan ten rows and hold them in your head.

export const PerfBand = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  margin-bottom: 20px;
`;

export const PerfHeadline = styled.div`
  background: ${({ theme }) => theme.colors.navy200};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 18px;
`;

export const PerfHeadlineTop = styled.div`
  align-items: baseline;
  display: flex;
  gap: 8px;
  justify-content: space-between;
`;

export const PerfBigValue = styled.span<{ $tone: TStatTone }>`
  color: ${({ $tone, theme }) => theme.colors[TONE_COLOR[$tone]]};
  font-size: 34px;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1;
`;

export const PerfLabel = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

// Ten ticks, one per player, ordered by this stat. Yours is lit. The shape of
// the lobby is the point: 2nd of 10 in a tight lobby and 2nd in a blowout are
// different games, and a bare rank number hides that.
export const PerfScale = styled.div`
  display: flex;
  gap: 3px;
`;

export const PerfTick = styled.span<{ $mine: boolean; $tone: TStatTone }>`
  background: ${({ $mine, $tone, theme }) =>
    $mine ? theme.colors[TONE_COLOR[$tone]] : theme.colors.chalkAlpha20};
  border-radius: 2px;
  flex: 1 1 0;
  height: ${({ $mine }) => ($mine ? "10px" : "6px")};
  // Ticks share a baseline so the lit one grows upward rather than recentring.
  align-self: flex-end;
`;

export const PerfRank = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;

  b {
    color: ${({ theme }) => theme.colors.ice};
    font-weight: 800;
  }
`;

// Self-contained tiles rather than a two-column table of label-left/value-right
// rows. In the table the pair on one visual line looked related and was not, and
// the rank sat so far from its own value that "+4" and "2nd" read as two facts.
// Here each tile carries label, value and context in one block, so the flow
// order stops mattering.
export const PerfTiles = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fit, minmax(148px, 1fr));
  margin-bottom: 20px;
`;

// Deliberately quieter than PerfHeadline: no border, smaller value. The band at
// the top stays the peak and these read as its supporting detail.
export const PerfTile = styled.div`
  background: ${({ theme }) => theme.colors.navy300};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
`;

export const PerfTileValue = styled.span<{ $tone: TStatTone }>`
  color: ${({ $tone, theme }) => theme.colors[TONE_COLOR[$tone]]};
  font-size: 20px;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.1;
`;

// The denominator or the lobby placing, on its own line and at its own weight,
// so it can never be mistaken for part of the value above it.
export const PerfTileSub = styled.span`
  color: ${({ theme }) => theme.colors.chalkAlpha80};
  font-size: 11px;
  font-variant-numeric: tabular-nums;
`;

export const PerfSectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin: 0 0 10px;
  text-transform: uppercase;
`;

// --- Round strip ------------------------------------------------------------
// Built entirely from round numbers the page already fetches and threw away:
// opening duels, multi-kills, clutches and the rounds that produced a moment.

// Each mark its own hue rather than shades of one: a round can carry several at
// once and they have to stay separable when stacked. Clutches are not here
// because the payload gives no round number for them.
export const ROUND_MARK_COLOR = {
  opening: "lime",
  multi: "turbo",
  moment: "ice",
} as const;

export type TRoundMark = keyof typeof ROUND_MARK_COLOR;

export const ROUND_MARK_LABEL: Record<TRoundMark, string> = {
  opening: "Opening kill",
  multi: "Multi-kill",
  moment: "Highlight",
};

export const RoundStrip = styled.div`
  display: grid;
  gap: 4px;
  // Fixed tracks so wrapped rows line up in columns instead of ending ragged.
  grid-template-columns: repeat(auto-fill, minmax(34px, 1fr));
  margin-bottom: 12px;
`;

// An empty round draws no container at all. With 25+ rounds the old version
// gave every single one a bordered box, so the rounds where something actually
// happened had nothing to stand out against.
export const RoundCell = styled.span<{ $active: boolean }>`
  align-items: center;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.navy100 : "transparent"};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 6px 4px;
`;

export const RoundNumber = styled.span<{ $active: boolean }>`
  color: ${({ $active, theme }) =>
    $active ? theme.colors.ice : theme.colors.chalkAlpha40};
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  font-weight: ${({ $active }) => ($active ? 800 : 600)};
  text-align: center;
`;

export const RoundMarks = styled.span`
  display: flex;
  gap: 2px;
  justify-content: center;
  min-height: 6px;
`;

export const RoundDot = styled.span<{ $kind: TRoundMark }>`
  background: ${({ $kind, theme }) => theme.colors[ROUND_MARK_COLOR[$kind]]};
  border-radius: 50%;
  flex-shrink: 0;
  height: 6px;
  width: 6px;
`;

export const RoundLegend = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 6px 16px;
  margin-bottom: 20px;

  span {
    align-items: center;
    display: inline-flex;
    gap: 6px;
  }
`;

// Status for the Match VOD tab, readable before the tab is opened. Entitlement
// deliberately does not change it: a free player sees that the recording exists
// and is told about the plan inside the tab, rather than being shown a locked
// tab and given no reason to look.
export const TabBadge = styled.span<{ $ready: boolean }>`
  background: ${({ $ready, theme }) =>
    $ready ? theme.colors.limeAlpha20 : theme.colors.chalkAlpha10};
  border-radius: 999px;
  color: ${({ $ready, theme }) =>
    $ready ? theme.colors.lime : theme.colors.chalk};
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 1;
  padding: 3px 6px;
  text-transform: uppercase;
  white-space: nowrap;
`;

// --- VOD player frame -------------------------------------------------------
// Reads as a video player, not a marketing panel: real frame, real 16:9, a
// centred play button and a control bar. A locked VOD looks exactly the same,
// because the point is that the player already has this recording and only
// needs access to it. The lock is stated in a chip, not by hiding the play.

export const VodPlayer = styled.div<{ $art?: string }>`
  aspect-ratio: 16 / 9;
  background: ${({ $art }) =>
      $art ? `url(${$art}) center / cover no-repeat` : "none"},
    ${({ theme }) => theme.colors.midnight};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 10px;
  overflow: hidden;
  position: relative;

  // Chrome scrims top and bottom, plus the banner's own pool-of-shade device
  // recentred on the play button, so the one thing to press sits in its own
  // pocket of contrast instead of competing with the brightest part of a map.
  &::after {
    background: radial-gradient(
        ellipse 46% 58% at 50% 50%,
        ${({ theme }) => theme.colors.midnightAlpha70} 0%,
        transparent 70%
      ),
      linear-gradient(
        180deg,
        ${({ theme }) => theme.colors.midnightAlpha80} 0%,
        transparent 32%
      ),
      linear-gradient(
        0deg,
        ${({ theme }) => theme.colors.midnightAlpha90} 0%,
        transparent 42%
      );
    content: "";
    inset: 0;
    pointer-events: none;
    position: absolute;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  // The one authored moment on this surface: pointing at the frame eases the
  // fog down, as if the footage is about to resolve, and the play button comes
  // to meet you. Blur is the material the locked state is made of, so moving it
  // says "this is nearly yours" in a way a colour or a shadow could not.
  @media (prefers-reduced-motion: no-preference) {
    [data-vod-art],
    [data-vod-play] {
      transition:
        filter 320ms cubic-bezier(0.16, 1, 0.3, 1),
        transform 320ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    &:hover [data-vod-art="preview"],
    &:focus-within [data-vod-art="preview"] {
      filter: blur(5px) brightness(0.95) grayscale(0.45);
    }

    &:hover [data-vod-art="teaser"],
    &:focus-within [data-vod-art="teaser"] {
      filter: blur(11px) brightness(0.52) grayscale(0.45);
    }

    &:hover [data-vod-play],
    &:focus-within [data-vod-play] {
      transform: translate(-50%, -50%) scale(1.06);
    }
  }
`;

// The match-history treatment, same two layers and the same numbers: the
// player's own map art blurred hard underneath, a real CS2 frame from the
// bucket blurred more gently on top. Together they read as footage sitting
// behind frosted glass rather than as a placeholder. Bleed clears roughly 3x
// the blur radius, or the faded edge lands inside the box as a dark outline.
export const VodTeaserArt = styled.div<{ $art?: string }>`
  background-image: ${({ $art }) => ($art ? `url(${$art})` : "none")};
  background-position: center;
  background-size: cover;
  filter: blur(14px) brightness(0.42) grayscale(1);

  // Doubled to out-specify VodPlayer's "> *" rule, which would otherwise force
  // these back to position: relative and lift them above the scrim.
  && {
    inset: -36px;
    position: absolute;
    z-index: 0;
  }
`;

export const VodPreviewArt = styled.div<{ $art?: string }>`
  background-image: ${({ $art }) => ($art ? `url(${$art})` : "none")};
  background-position: center;
  background-size: cover;
  filter: blur(9px) brightness(0.82) grayscale(1);

  // Doubled to out-specify VodPlayer's "> *" rule, which would otherwise force
  // these back to position: relative and lift them above the scrim.
  && {
    inset: -24px;
    position: absolute;
    z-index: 0;
  }
`;

export const VodPlayerTop = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  left: 0;
  padding: 14px 16px;
  position: absolute;
  right: 0;
  top: 0;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    padding: 10px 12px;
  }
`;

export const VodPlayerTitle = styled.span`
  color: ${({ theme }) => theme.colors.iceAlpha75};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-shadow: 0 1px 4px ${({ theme }) => theme.colors.midnightAlpha90};
`;

export const VodLockChip = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.midnightAlpha80};
  border: 1px solid ${({ theme }) => theme.colors.tierPlatAlpha40};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.ice};
  display: inline-flex;
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 800;
  gap: 5px;
  letter-spacing: 0.06em;
  padding: 5px 10px;
  text-transform: uppercase;

  svg {
    color: ${({ theme }) => theme.colors.tierPlat};
  }
`;

// A real play button, present whether or not the VOD is unlocked. Replacing it
// with a padlock was the version that made the tab feel like an advert; this
// one makes the recording feel like it is already there.
export const VodPlayButton = styled.button<{ $locked?: boolean }>`
  align-items: center;
  background: ${({ $locked, theme }) =>
    $locked ? theme.colors.tierPlat : theme.colors.envy};
  border: 0;
  border-radius: 50%;
  // Three rings of its own colour rather than a drop shadow alone: it reads as
  // lit from within, which is what makes it the subject of the frame.
  box-shadow:
    0 0 0 8px
      ${({ $locked, theme }) =>
        $locked ? theme.colors.tierPlatAlpha25 : theme.colors.envyAlpha20},
    0 0 0 18px
      ${({ $locked, theme }) =>
        $locked ? theme.colors.tierPlatAlpha15 : theme.colors.envyAlpha10},
    0 10px 34px ${({ theme }) => theme.colors.midnightAlpha80};
  cursor: pointer;
  display: flex;
  height: 84px;
  justify-content: center;
  left: 50%;
  padding: 0;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 84px;

  // A real icon rather than the CSS-triangle trick: it sits on the baseline
  // properly at this size and needs no optical nudge to look centred.
  svg {
    // Ice on purple, midnight on envy: each is the readable one on its own fill.
    color: ${({ $locked, theme }) =>
      $locked ? theme.colors.ice : theme.colors.midnight};
    margin-left: 3px;
  }

  // Scale lives on the parent's hover so the whole frame reacts as one thing.
  &:hover {
    filter: brightness(1.08);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.ice};
    outline-offset: 6px;
  }

  // A 16:9 frame is only ~200px tall on a phone, where an 84px button ringed to
  // 120px would touch both chrome bars. Smaller button, thinner rings.
  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    box-shadow:
      0 0 0 5px
        ${({ $locked, theme }) =>
          $locked ? theme.colors.tierPlatAlpha25 : theme.colors.envyAlpha20},
      0 0 0 11px
        ${({ $locked, theme }) =>
          $locked ? theme.colors.tierPlatAlpha15 : theme.colors.envyAlpha10},
      0 8px 24px ${({ theme }) => theme.colors.midnightAlpha80};
    height: 56px;
    width: 56px;

    svg {
      height: 28px;
      width: 28px;
    }
  }
`;

export const VodControls = styled.div`
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  left: 0;
  padding: 0 16px 14px;
  position: absolute;
  right: 0;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    gap: 6px;
    padding: 0 12px 10px;
  }
`;

// The round markers double as the scrub track: it is the real shape of the
// match, so it says "this is a recording of your game" better than a fake
// progress bar would.
export const VodScrub = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 2px;
  height: 26px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    height: 16px;
  }
`;

export const VodMetaRow = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.iceAlpha75};
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  font-weight: 600;
  gap: 2px 10px;
  justify-content: space-between;

  // Neither label truncates the other; the round count is the one that matters
  // and it wraps first rather than being clipped.
  > span {
    min-width: 0;
  }

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    font-size: 11px;
  }
`;

// --- Tab intro --------------------------------------------------------------
// Every panel opens the same way: what this is, then one line on why it exists.
// The tab strip names things in 11px chrome, which is enough to navigate by and
// not enough to explain anything, so each panel says it once at full size.
// Title first with no label above it: a kicker over a heading is an eyebrow.

export const TabIntro = styled.header`
  margin-bottom: 20px;
  max-width: 62ch;
`;

export const TabTitle = styled.h1`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  margin: 0 0 6px;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    font-size: 21px;
  }
`;

export const TabDesc = styled.p`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`;

// --- VOD timeline -----------------------------------------------------------
// Below the frame rather than inside it, the way the viewer itself lays out:
// video, then the round seeker, then the moments rail. Both rows are the same
// grid, so a moment sits in the column of the round it happened in. Flex with
// `flex: 1 1 0` could not guarantee that once a row held different content.

export const VodTimeline = styled.div`
  margin-top: 14px;
`;

export const VodTrack = styled.div<{ $rounds: number }>`
  align-items: flex-end;
  display: grid;
  gap: 2px;
  grid-template-columns: repeat(${({ $rounds }) => $rounds}, minmax(0, 1fr));
  height: 26px;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    height: 18px;
  }
`;

export const VodMomentRow = styled.div<{ $rounds: number }>`
  align-items: center;
  display: grid;
  gap: 2px;
  grid-template-columns: repeat(${({ $rounds }) => $rounds}, minmax(0, 1fr));
  margin-top: 6px;
  min-height: 20px;
`;

// Sits in its round's column. The weapon icon when the moment has one, so the
// rail says what happened rather than only that something did.
export const VodMomentMark = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  display: flex;
  justify-content: center;
  min-width: 0;

  img {
    height: 14px;
    max-width: 100%;
    object-fit: contain;
  }

  // The fallback when a moment carries no icon.
  &[data-plain]::after {
    background: ${({ theme }) => theme.colors.envy};
    border-radius: 50%;
    content: "";
    height: 6px;
    width: 6px;
  }
`;

export const VodTimelineCaption = styled.div`
  align-items: baseline;
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 2px 12px;
  justify-content: space-between;
  margin-top: 10px;

  b {
    color: ${({ theme }) => theme.colors.ice};
    font-weight: 700;
  }
`;

// --- Duels table ------------------------------------------------------------
// One ranked table instead of two featured cards plus a list. The old shape
// rendered "your best duel" and "your nemesis" with identical anatomy side by
// side, so neither led, and it ran two interaction models over the same data:
// the pair permanently open, the rest click-to-open. Everything is a row now,
// ranked, with the head-to-head numbers on the row rather than behind a click.

// Header and rows share one template so a column can never drift between them.
const duelGrid = css`
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) 54px 62px 62px 56px 22px;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    gap: 10px;
    grid-template-columns: minmax(0, 1fr) 50px 58px 22px;
  }
`;

export const DuelHead = styled.div`
  ${duelGrid};
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 0 16px 8px;
  text-transform: uppercase;
`;

export const DuelHeadNumeric = styled.span`
  text-align: right;
`;

export const DuelRowToggle = styled.button<{ $open?: boolean }>`
  ${duelGrid};
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  font-family: inherit;
  padding: 14px 16px;
  text-align: left;
  transition: background 0.15s ease;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha06};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: -2px;
  }

  svg {
    color: ${({ theme }) => theme.colors.chalk};
    transition: transform 0.15s ease;
    transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
  }

  @media (prefers-reduced-motion: reduce) {
    svg {
      transition: none;
    }
  }
`;

export const DuelOpponent = styled.span`
  align-items: center;
  display: flex;
  gap: 12px;
  min-width: 0;

  // Scaled here rather than on Avatar itself, which the scoreboard also uses at
  // its own size. A row is the primary object on this tab, so it carries a
  // portrait rather than a token.
  ${Avatar}, ${AvatarFallback} {
    font-size: 15px;
    height: 40px;
    width: 40px;
  }

  ${DuelName} {
    flex: 0 1 auto;
    font-size: 16px;
  }

  ${RankBadgeWrap} {
    flex-shrink: 0;
  }

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    gap: 10px;

    ${Avatar}, ${AvatarFallback} {
      height: 34px;
      width: 34px;
    }

    ${DuelName} {
      font-size: 15px;
    }

    // Four things do not fit a phone's opponent column. Rank goes first: the
    // Best/Nemesis tag is the one carrying the ranking this table is sorted by.
    ${RankBadgeWrap} {
      display: none;
    }
  }
`;

// The tally is the duel itself, so it keeps win/loss colour. The stat columns
// beside it are full-match numbers and are toned separately.
export const DuelScore = styled.span<{ $won: boolean }>`
  color: ${({ $won, theme }) =>
    $won ? theme.colors.envy : theme.colors.alert};
  font-size: 16px;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  text-align: right;
`;

// Weight and lightness rather than hue. Three coloured numbers per row, on top
// of a tinted row and a coloured tally, was most of why this table was loud:
// the sign already says which way the stat went.
export const DuelDelta = styled.span<{ $lead?: boolean | null }>`
  color: ${({ $lead, theme }) =>
    $lead === null || $lead === undefined
      ? theme.colors.chalkAlpha60
      : $lead
      ? theme.colors.ice
      : theme.colors.chalk};
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  text-align: right;
`;

// Dropped first when the table narrows: rating is the one worth keeping.
export const DuelDeltaWide = styled(DuelDelta)`
  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    display: none;
  }
`;

export const DuelHeadWide = styled(DuelHeadNumeric)`
  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    display: none;
  }
`;

export const DuelTable = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 10px;
  overflow: hidden;
`;

// Tinted by outcome rather than striped by position. The table is already
// ranked best to worst, so this lands as a band of green at the top fading
// through neutral into red at the bottom: the same scanability zebra gives,
// except the colour means the thing the row is about.
export const DuelTableRow = styled.div<{ $tone: "won" | "lost" | "even" }>`
  background: ${({ $tone, theme }) =>
    $tone === "won"
      ? theme.colors.envyAlpha05
      : $tone === "lost"
      ? theme.colors.alertAlpha05
      : "transparent"};
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};

  &:first-child {
    border-top: 0;
  }
`;

// Explains once that the stat columns are the whole match, not the duel, so
// nobody reads "+0.61" as something that happened inside these kills.
export const DuelNote = styled.p`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  margin: 10px 0 0;
`;

// --- Performance identity ---------------------------------------------------
// Whose match this is, stated once at the top. The tab is called "Your
// Performance" for every viewer, so the face and the rank are what actually
// say which player these numbers belong to.

export const PerfIdentity = styled.div`
  align-items: center;
  display: flex;
  gap: 14px;
  margin-bottom: 10px;

  // 44 rather than 56: the tier frame is drawn for a ~40px avatar, and at 56
  // it sat inside the portrait instead of around it.
  ${Avatar}, ${AvatarFallback} {
    font-size: 17px;
    height: 44px;
    width: 44px;
  }

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    gap: 12px;

    ${Avatar}, ${AvatarFallback} {
      height: 46px;
      width: 46px;
    }
  }
`;

export const PerfWho = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-top: 4px;
  min-width: 0;
`;

export const PerfName = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// The overview's featured card is sized by a 260-340px column. Loose on a full
// width page it would stretch to a letterbox, so it keeps a column's measure.
export const BestHighlight = styled.div`
  max-width: 420px;
`;

// --- Top three --------------------------------------------------------------
// A podium, not three equal tiles: first place is wider and carries the medal
// colour, so the ranking is legible before a single number is read. Equal cards
// would have made the reader compare three ratings to find out who won.

export const PodiumGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 1.35fr 1fr 1fr;
  margin-bottom: 20px;

  // Below the two-column layout the podium stacks rather than squeezing three
  // cards into a phone's width.
  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    grid-template-columns: 1fr;
  }
`;

// A link, not a panel: the card is the most direct route to whoever is on it.
// The medal bar that used to sit on the top edge is gone. As a pseudo element
// inside a rounded, bordered box it could never meet the corners cleanly, and
// it was one more thing to read on a card that was already carrying too much.
// The place numeral keeps the medal colour, which is enough to rank three.
export const PodiumCard = styled(Link)<{
  $place: 1 | 2 | 3;
  $isViewer: boolean;
}>`
  background: ${({ theme }) => theme.colors.navy200};
  border: 1px solid
    ${({ $isViewer, theme }) =>
      $isViewer ? theme.colors.envyAlpha40 : theme.colors.chalkAlpha10};
  border-radius: 10px;
  color: inherit;
  display: flex;
  flex-direction: column;
  // Two lines of content, so the card no longer needs the height three had.
  gap: 8px;
  padding: 11px 12px;
  text-decoration: none;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.navy100};
    border-color: ${({ $isViewer, theme }) =>
      $isViewer ? theme.colors.envy : theme.colors.chalkAlpha40};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const PodiumTop = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  min-width: 0;
`;

export const PodiumPlace = styled.span<{ $place: 1 | 2 | 3 }>`
  color: ${({ $place, theme }) =>
    $place === 1
      ? theme.colors.timelineGold
      : $place === 2
      ? theme.colors.chalk
      : theme.colors.bronze};
  flex-shrink: 0;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  letter-spacing: 0.04em;
`;

export const PodiumWho = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const PodiumName = styled.span`
  font-size: 14px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PodiumRating = styled.div`
  align-items: baseline;
  display: flex;
  gap: 7px;
`;

export const PodiumRatingValue = styled.span<{ $tone: TStatTone }>`
  color: ${({ $tone, theme }) => theme.colors[TONE_COLOR[$tone]]};
  font-size: 22px;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1;
`;

// The supporting numbers stay in one quiet row: they are context for the
// rating above, not four more things to rank people by.
export const PodiumStats = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  gap: 4px 12px;

  b {
    color: ${({ theme }) => theme.colors.ice};
    font-weight: 700;
  }
`;

export const PodiumYou = styled.span`
  background: ${({ theme }) => theme.colors.envyAlpha20};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.envy};
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
  padding: 3px 7px;
  text-transform: uppercase;
`;

// The Steam persona, kept beside the Allstar name rather than replaced by it:
// the account name is who they are on the site, the Steam name is who their
// teammates saw in the server, and on a match page both are worth having.
export const AltName = styled.span`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  font-size: 0.8em;
  font-weight: 500;
`;

// One crown on the best player's avatar, rather than 1/2/3 markers down the
// column. Three medals asked the reader to decode a second ranking while they
// were already reading a sorted table; a crown is a single fact, read at a
// glance, and it costs the row no horizontal space at all.
export const AvatarCrown = styled.span`
  color: ${({ theme }) => theme.colors.timelineGold};
  display: flex;
  // Sits on the avatar's top edge, tipped the way a crown sits on a head.
  left: 50%;
  pointer-events: none;
  position: absolute;
  top: -7px;
  transform: translateX(-50%) rotate(-12deg);
  // Above AvatarV2's own border overlay and badge.
  z-index: 3;

  svg {
    filter: drop-shadow(
      0 1px 2px ${({ theme }) => theme.colors.midnightAlpha90}
    );
    height: 13px;
    width: 13px;
  }
`;

// Gives the crown something to anchor to without disturbing the avatar itself.
export const AvatarCrownWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  position: relative;
`;

// --- All Clips tab -------------------------------------------------------

export const AllClipsHead = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  justify-content: space-between;
  margin-bottom: 12px;
`;

// One filter idiom on this page: the chips borrow the sort strip's language so
// the tab does not introduce a second way of saying "narrow this down".
export const PlayerChipRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 20px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const PlayerChip = styled.button<{ $active?: boolean }>`
  align-items: center;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.chalkAlpha10 : "transparent"};
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.chalkAlpha20 : "transparent"};
  border-radius: 999px;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.ice : theme.colors.chalk};
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  gap: 6px;
  max-width: 180px;
  padding: 5px 12px 5px 5px;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// Players without an avatar keep the same chip height, so the row
// does not step up and down as it scrolls.
export const ChipAvatar = styled.img`
  border-radius: 50%;
  flex-shrink: 0;
  height: 22px;
  object-fit: cover;
  width: 22px;
`;

export const ChipDot = styled.span`
  background: ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 50%;
  flex-shrink: 0;
  height: 22px;
  width: 22px;
`;

export const PlayerSection = styled.section`
  margin-bottom: 28px;
`;

export const PlayerSectionHead = styled.div`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 8px;
`;

export const SectionAvatar = styled.img`
  border-radius: 50%;
  flex-shrink: 0;
  height: 28px;
  object-fit: cover;
  width: 28px;
`;

export const SectionPlayerName = styled.h3`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 15px;
  font-weight: 700;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SectionCount = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 600;
  margin-right: auto;
`;

// The team a player was on, not a score. Kept to a hairline pill so it reads as
// a label rather than competing with the scoreboard's own tinting.
export const TeamMark = styled.span<{ $viewerTeam?: boolean }>`
  border: 1px solid
    ${({ theme, $viewerTeam }) =>
      $viewerTeam ? theme.colors.envyAlpha40 : theme.colors.chalkAlpha20};
  border-radius: 4px;
  color: ${({ theme, $viewerTeam }) =>
    $viewerTeam ? theme.colors.envy : theme.colors.chalk};
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  padding: 2px 6px;
  text-transform: uppercase;
`;

export const ClipGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
`;

// Holds the row height before a section has loaded, so the page does not jump
// as lazily mounted sections resolve underneath the scroll position.
export const SkeletonCell = styled.div`
  animation: matchClipPulse 1.6s ease-in-out infinite;
  aspect-ratio: 16 / 9;
  background: ${({ theme }) => theme.colors.navy300};
  border-radius: ${THUMB_RADIUS};

  @keyframes matchClipPulse {
    0%,
    100% {
      opacity: 0.5;
    }
    50% {
      opacity: 0.8;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// --- Scoreboard clip indicator -------------------------------------------

// The crown on the avatar already means "top rating in this match", so the
// standout play needs its own mark and its own place.
export const StandoutStar = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.bestMoment};
  display: inline-flex;

  svg {
    height: 10px;
    width: 10px;
  }
`;

// --- All Highlights: the lobby top row -----------------------------------

// Bigger cells than the wall below it, because this row is the answer to
// "what should I watch" and the wall is the archive.
export const LobbyTopGrid = styled.div<{ $lead?: boolean }>`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  margin-bottom: 0;

  // Cards top-align rather than stretching to the tallest in their row, so the
  // wider lead card cannot pull its neighbours out of shape.
  align-items: start;

  // The lead card earns its place where the row is long enough to carry the
  // asymmetry. Columns only, because row spans depend on content-sized tracks
  // lining up and they do not.
  ${({ $lead }) =>
    $lead &&
    css`
      @media (min-width: ${({ theme }) => theme.breaks.theater}) {
        > *:first-child {
          grid-column: span 2;
        }
      }
    `}

  @media (prefers-reduced-motion: no-preference) {
    > * {
      animation: lobbyCardIn 380ms cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    > *:nth-child(2) {
      animation-delay: 40ms;
    }
    > *:nth-child(3) {
      animation-delay: 80ms;
    }
    > *:nth-child(4) {
      animation-delay: 120ms;
    }
    > *:nth-child(5) {
      animation-delay: 160ms;
    }
    > *:nth-child(6) {
      animation-delay: 200ms;
    }
  }

  @keyframes lobbyCardIn {
    from {
      filter: blur(6px);
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      filter: blur(0);
      opacity: 1;
      transform: none;
    }
  }
`;

// Mirrors LobbyTopGrid's tracks so the row does not resize when it resolves.
export const LobbySkeletonGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
`;

export const WallHead = styled.h3`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  font-size: 12px;
  font-weight: 800;
  gap: 8px;
  letter-spacing: 0.06em;
  margin: 0 0 12px;
  padding-bottom: 8px;
  text-transform: uppercase;

  b {
    color: ${({ theme }) => theme.colors.envy};
    font-weight: 800;
  }

  small {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0;
    margin-left: auto;
    text-transform: none;
  }

  // A control group in the head sits right, against the section it governs.
  > div[role="group"] {
    margin-left: auto;
  }
`;

// Sits above the hero on the page ground, not floating over the art: it is a
// way back, not an action on this match. Quiet by default, ice on hover.
export const BackLink = styled(Link)`
  align-items: center;
  align-self: flex-start;
  color: ${({ theme }) => theme.colors.chalk};
  display: inline-flex;
  font-size: 13px;
  font-weight: 700;
  gap: 6px;
  letter-spacing: 0.02em;
  margin-bottom: 12px;
  text-decoration: none;
  transition: color 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 3px;
  }

  svg {
    flex-shrink: 0;
    transition: transform 0.15s ease;
  }

  &:hover svg {
    transform: translateX(-2px);
  }
`;

// Sits in the lobby heading on the overview, where the row is a preview and the
// full set lives on its own tab.
export const LobbySeeAll = styled.button`
  align-items: center;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: 12px;
  font-weight: 800;
  gap: 4px;
  letter-spacing: 0;
  margin-left: auto;
  padding: 0;
  text-transform: none;
  transition: color 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 3px;
  }

  svg {
    height: 13px;
    width: 13px;
  }
`;

// The overview mount is a band above the two-column grid, so it reads as the
// match's own lead rather than as a third column.
export const LobbyBand = styled.section`
  margin-bottom: 32px;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    margin-bottom: 24px;
  }
`;

// --- Moment tile identity -------------------------------------------------

// The avatar sits on the artwork rather than on a line beneath it, so the tile
// stays a single 16:9 object and the person is part of the frame.
export const TileIdentity = styled.span`
  align-items: center;
  display: flex;
  gap: 8px;
  min-width: 0;
`;

export const TileAvatar = styled.img`
  border: 1px solid ${({ theme }) => theme.colors.iceAlpha40};
  border-radius: 50%;
  flex-shrink: 0;
  height: 26px;
  object-fit: cover;
  width: 26px;
`;

export const TileLines = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

// --- Split spotlight: the match's best highlight --------------------------

// A div, not a button: it holds a share control, and a button inside a button
// is invalid markup. SpotlightAction below is the real control.
export const SpotlightCell = styled.div`
  background: ${({ theme }) => theme.colors.navy300};
  border-radius: ${THUMB_RADIUS};
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
  display: grid;
  grid-template-columns: 1fr;
  // One clip for the card; the art inside no longer rounds itself. Same measured
  // corner-halo fix as CardThumb.
  clip-path: inset(0 round ${THUMB_RADIUS});
  position: relative;
  text-align: left;
  transition:
    box-shadow 0.15s ease,
    background 0.15s ease;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.navy200};
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.envyAlpha40};
  }

  &:hover span[data-cta] {
    filter: brightness(1.1);
  }

  // Art leads when there is only one column; the panel becomes a caption.
  grid-template-areas: "art" "panel";

  // The areas must be declared at every width. Named grid-area values with no
  // matching template resolve to lines that do not exist, and both children get
  // auto-placed outside the declared columns.
  @media (min-width: ${({ theme }) => theme.breaks.theater}) {
    grid-template-areas: "panel art";
    grid-template-columns: minmax(0, 210px) minmax(0, 1fr);
  }
`;

export const SpotlightAction = styled.button`
  background: none;
  border: 0;
  border-radius: ${THUMB_RADIUS};
  cursor: pointer;
  inset: 0;
  padding: 0;
  position: absolute;
  z-index: 2;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
  }
`;

export const SpotlightPanel = styled.span`
  display: flex;
  flex-direction: column;
  gap: 6px;
  grid-area: panel;
  min-width: 0;
  padding: 16px;
  pointer-events: none;

  @media (min-width: ${({ theme }) => theme.breaks.theater}) {
    justify-content: center;
  }
`;

export const SpotlightAvatar = styled.img`
  border: 2px solid ${({ theme }) => theme.colors.envyAlpha40};
  border-radius: 50%;
  height: 56px;
  margin-bottom: 4px;
  object-fit: cover;
  width: 56px;

  @media (max-width: ${({ theme }) => theme.breaks.theater}) {
    height: 40px;
    width: 40px;
  }
`;

export const SpotlightName = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  display: flex;
  font-size: 18px;
  font-weight: 800;
  gap: 6px;
  letter-spacing: -0.01em;
  min-width: 0;

  b {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const SpotlightTitle = styled.span`
  color: ${({ theme }) => theme.colors.iceAlpha75};
  display: -webkit-box;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

export const SpotlightMeta = styled.span<{
  $state: "ready" | "rendering" | "failed" | "idle";
}>`
  align-items: center;
  color: ${({ $state, theme }) =>
    $state === "rendering"
      ? theme.colors.envy
      : $state === "failed"
      ? theme.colors.alert
      : theme.colors.chalk};
  display: flex;
  font-size: 12px;
  font-weight: 600;
  gap: 5px;

  img {
    height: 13px;
    width: auto;
  }
`;

export const SpotlightArt = styled.span<{ $image?: string; $dim?: boolean }>`
  align-items: center;
  aspect-ratio: 16 / 9;
  background: ${({ $image }) =>
      $image ? `url(${$image}) center / cover no-repeat` : "none"},
    ${({ theme }) => theme.colors.midnightAlpha80};
  display: flex;
  grid-area: art;
  justify-content: center;
  pointer-events: none;
  position: relative;
  width: 100%;

  &::after {
    backdrop-filter: ${({ $dim }) => ($dim ? "blur(9px)" : "none")};
    -webkit-backdrop-filter: ${({ $dim }) => ($dim ? "blur(9px)" : "none")};
    background: ${({ theme, $dim }) =>
      $dim ? theme.colors.midnightAlpha60 : theme.colors.midnightAlpha30};
    content: "";
    inset: 0;
    position: absolute;
  }

  em {
    background: ${({ theme }) => theme.colors.midnightAlpha80};
    border-radius: 4px;
    bottom: 8px;
    font-size: 11px;
    font-style: normal;
    font-weight: 700;
    padding: 2px 6px;
    position: absolute;
    right: 8px;
    z-index: 2;
  }
`;

// Sits on the artwork at the corner opposite the duration. Always present
// rather than hover-only: a share you cannot see is a share nobody uses, and
// hover does not exist on touch.
export const TileShare = styled.button<{ $copied?: boolean; $large?: boolean }>`
  align-items: center;
  background: ${({ theme }) => theme.colors.midnightAlpha80};
  border: 0;
  border-radius: 50%;
  color: ${({ theme, $copied }) =>
    $copied ? theme.colors.envy : theme.colors.iceAlpha75};
  cursor: pointer;
  display: inline-flex;
  height: ${({ $large }) => ($large ? "32px" : "26px")};
  justify-content: center;
  padding: 0;
  position: absolute;
  right: ${({ $large }) => ($large ? "8px" : "6px")};
  top: ${({ $large }) => ($large ? "8px" : "6px")};
  transition:
    background 0.15s ease,
    color 0.15s ease;
  width: ${({ $large }) => ($large ? "32px" : "26px")};
  z-index: 3;

  &:hover {
    background: ${({ theme }) => theme.colors.midnight};
    color: ${({ theme, $copied }) =>
      $copied ? theme.colors.envy : theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  svg {
    height: ${({ $large }) => ($large ? "16px" : "14px")};
    width: ${({ $large }) => ($large ? "16px" : "14px")};
  }
`;

// The tile stops being a button so it can hold one: a button inside a button is
// invalid markup. The frame is the box, the action fills it, share sits above.
export const TileFrame = styled.div`
  position: relative;

  // The call to action lives in a sibling of the button now, so the frame owns
  // the hover relationship.
  &:hover span[data-cta] {
    filter: brightness(1.1);
  }
`;

export const TileAction = styled.button`
  background: none;
  border: 0;
  border-radius: ${THUMB_RADIUS};
  cursor: pointer;
  inset: 0;
  padding: 0;
  position: absolute;
  z-index: 2;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
  }
`;

// Sits in flow above the board, so it takes a tone step instead of a shadow.
export const FeedbackBanner = styled.aside`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy300};
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha10};
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 10px 10px 10px 16px;
  position: relative;

  // Once the answers wrap under the question, the close button would trail
  // them, so it moves to the corner instead.
  @media (max-width: 600px) {
    padding: 14px 44px 14px 16px;
  }
`;

export const FeedbackBannerText = styled.p`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  min-width: 0;

  strong {
    color: ${({ theme }) => theme.colors.ice};
    font-size: 14px;
    font-weight: 700;
  }

  span {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 13px;
  }
`;

export const FeedbackBannerActions = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

export const FeedbackAnswer = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 999px;
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  gap: 6px;
  padding: 7px 14px;
  transition:
    background 0.15s ease,
    box-shadow 0.15s ease;
  white-space: nowrap;

  svg {
    color: ${({ theme }) => theme.colors.chalk};
    transition: color 0.15s ease;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha10};
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha40};

    svg {
      color: ${({ theme }) => theme.colors.envy};
    }
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

export const FeedbackBannerClose = styled.button`
  align-items: center;
  background: none;
  border: none;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  height: 32px;
  justify-content: center;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  width: 32px;

  @media (max-width: 600px) {
    position: absolute;
    right: 6px;
    top: 6px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.navy200};
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;
